import { Box, Chip, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2EvidenceStep } from "shared/sstv2MigrationEvidenceRun"

const markerColor = (state: string) =>
  state === "done"
    ? "success.main"
    : state === "running"
      ? "info.main"
      : state === "error"
        ? "error.main"
        : "grey.400"

/**
 * Vertical 6-step progress for a synthetic-migration evidence run. Custom
 * (not MUI Stepper) so each step renders its own state colour + the events it
 * emitted + the observed/sent counts — the run's live proof-of-work.
 */
export const Sstv2EvidenceRunStepper: FC<{ steps: Sstv2EvidenceStep[] }> = ({
  steps,
}) => {
  const { t } = useTranslation()
  return (
    <Stack data-ui="UI-SL2W-266">
      {steps.map((step, i) => (
        <Stack key={step.key} direction="row" spacing={1.5}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: markerColor(step.state),
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                flex: "none",
              }}
            >
              {step.state === "done" ? "✓" : step.state === "error" ? "!" : i + 1}
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ flex: 1, width: 2, minHeight: 18, bgcolor: "divider", mt: 0.5 }} />
            )}
          </Box>

          <Box sx={{ pb: i < steps.length - 1 ? 2 : 0, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
              {step.title}
            </Typography>
            <Typography
              sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}
            >
              {step.method} {step.path || "…"}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
              {step.state === "running" && (
                <Chip size="small" color="info" label={t("running")} />
              )}
              {step.httpStatus && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={step.httpStatus === "ok" ? "default" : "error"}
                  label={step.httpCode ? `HTTP ${step.httpCode}` : step.httpStatus}
                />
              )}
              {(step.events || []).map((e) => (
                <Chip key={e} size="small" color="primary" variant="outlined" label={e} />
              ))}
              {step.isStatus && (
                <Chip size="small" variant="outlined" label={t("noSqsEvents")} />
              )}
            </Stack>
            {!step.isStatus && (step.state === "done" || step.state === "error") && (
              <Typography sx={{ fontSize: "0.76rem", color: "text.secondary", mt: 0.5 }}>
                {t("observed")}: {step.observedCount} · {t("sent")}:{" "}
                {step.sentMetric ?? "—"}
              </Typography>
            )}
            {step.errorMessage && (
              <Typography sx={{ fontSize: "0.76rem", color: "error.main", mt: 0.5 }}>
                {step.errorMessage}
              </Typography>
            )}
          </Box>
        </Stack>
      ))}
    </Stack>
  )
}

export default Sstv2EvidenceRunStepper
